import { FormGroup } from '@angular/forms';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { addRemoveError } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { isEqual } from 'lodash-es';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { toNum } from '@shared/helpers/math.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import {
    MyAllAmminUnicoEntity,
    MyAllElemCollegioSindacaleEntity,
    MyAllElemConsiglioAmministrazioneEntity,
    MyAllElemLiquidatoriEntity,
} from '../../../../models/interfaces/revisione.interface';
import {
    getNormeSrlControl,
    getAmmControlloControl,
    normeSrlCkHelper,
    checkCkNormeValue,
    checkAllElemCollegioSindacale,
} from './helpers/norme-srl-form-validators.helper';

export const normeSrlTipoAmmRules: FormGroupRules = {
    my_CkSrlTipoAmministrazione: (form: FormGroup) => {
        const my_CkSrlTipoAmministrazioneControl = getNormeSrlControl(form, 'my_CkSrlTipoAmministrazione');
        const my_CkSrlTipoAmministrazione = toNum(my_CkSrlTipoAmministrazioneControl.value);
        const my_AllElemConsiglioAmministrazione: MyAllElemConsiglioAmministrazioneEntity[] = getAmmControlloControl(
            form,
            'my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione'
        )?.value;
        const my_AllAmminUnico: MyAllAmminUnicoEntity[] = getAmmControlloControl(form, 'my_AllAmminUnico')?.value;
        const my_AllElemLiquidatori: MyAllElemLiquidatoriEntity = getAmmControlloControl(form, 'my_AllLiquidatori.my_AllElemLiquidatori')?.value;
        const errors = my_CkSrlTipoAmministrazioneControl.errors ? my_CkSrlTipoAmministrazioneControl.errors : {};

        addRemoveError(isNilOrEmpty(my_AllElemConsiglioAmministrazione) && my_CkSrlTipoAmministrazione === 1 && checkCkNormeValue(form), errors, 'my_CkSrlTipoAmministrazione', {
            status: 'Inserire il Consiglio di Amministrazione.',
            message: "Il Consiglio di Amministrazione è obbligatorio quando è stato scelto per l'amministrazione.",
        });

        addRemoveError(isNilOrEmpty(my_AllAmminUnico) && my_CkSrlTipoAmministrazione === 2 && checkCkNormeValue(form), errors, 'my_CkSrlTipoAmministrazione', {
            status: "Inserire l'Amministratore Unico.",
            message: "L'Amministratore Unico è obbligatorio quando è stato scelto per l'amministrazione.",
        });

        addRemoveError(isNilOrEmpty(my_AllElemLiquidatori) && my_CkSrlTipoAmministrazione === 3 && checkCkNormeValue(form), errors, 'my_CkSrlTipoAmministrazione', {
            status: 'Inserire i Liquidatori',
            message: "E' stato selezionato Liquidatori come tipo di amministrazione ma non si è specificato ancora alcun Liquidatore.",
        });

        addRemoveError(!isNilOrEmpty(my_AllAmminUnico) && my_CkSrlTipoAmministrazione === 1 && checkCkNormeValue(form), errors, 'my_CkSrlTipoAmministrazione', {
            status: "Eliminare l'Amministratore Unico.",
            message: "Si è scelto il Consiglio di Amministrazione ma è ancora presente l'Amministratore Unico.",
        });

        addRemoveError(!isNilOrEmpty(my_AllElemLiquidatori) && my_CkSrlTipoAmministrazione === 1 && checkCkNormeValue(form), errors, 'my_CkSrlTipoAmministrazione', {
            status: 'Eliminare i Liquidatori',
            message: "E' stato selezionato Consiglio di Amministrazione come tipo di amministrazione ma è presente almeno un Liquidatore.",
        });

        addRemoveError(!isNilOrEmpty(my_AllElemConsiglioAmministrazione) && my_CkSrlTipoAmministrazione === 2 && checkCkNormeValue(form), errors, 'my_CkSrlTipoAmministrazione', {
            status: 'Eliminare il Consiglio di Amministrazione.',
            message: "Si è scelto l'Amministratore Unico ma è ancora presente una persona nel Consiglio di Amministrazione.",
        });

        addRemoveError(!isNilOrEmpty(my_AllElemLiquidatori) && my_CkSrlTipoAmministrazione === 2 && checkCkNormeValue(form), errors, 'my_CkSrlTipoAmministrazione', {
            status: 'Eliminare i Liquidatori',
            message: "E' stato selezionato Amministratore Unico come tipo di amministrazione ma è ancora presente almeno un Liquidatore.",
        });

        addRemoveError(!isNilOrEmpty(my_AllElemConsiglioAmministrazione) && my_CkSrlTipoAmministrazione === 3 && checkCkNormeValue(form), errors, 'my_CkSrlTipoAmministrazione', {
            status: 'Eliminare il Consiglio di Amministrazione',
            message: 'Si è scelto Liquidatori come tipo di amministrazione ma sono presenti ancora elementi nel Consiglio di Amministrazione.',
        });

        addRemoveError(!isNilOrEmpty(my_AllAmminUnico) && my_CkSrlTipoAmministrazione === 3 && checkCkNormeValue(form), errors, 'my_CkSrlTipoAmministrazione', {
            status: "Eliminare l'Amministratore Unico",
            message: "Si è scelto Liquidatori come tipo di amministrazione ma è specificato l'Amministratore Unico.",
        });

        addRemoveError(isNilOrEmpty(my_CkSrlTipoAmministrazione) && checkCkNormeValue(form), errors, 'my_CkSrlTipoAmministrazione', {
            status: REQUIRED_STATUS,
        });

        my_CkSrlTipoAmministrazioneControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_SrlModalitaFormeAmmin: (form: FormGroup) => {
        const my_SrlModalitaFormeAmminControl = getNormeSrlControl(form, 'my_SrlModalitaFormeAmmin');
        const my_CkSrlTipoAmministrazione: string = getNormeSrlControl(form, 'my_CkSrlTipoAmministrazione').value;
        const my_CkSrlStatutoFormeAmmin: string = getNormeSrlControl(form, 'my_CkSrlStatutoFormeAmmin').value;
        const my_CkSrlStatutoFormeAmminDisg: string = getNormeSrlControl(form, 'my_CkSrlStatutoFormeAmminDisg').value;
        const errors = my_SrlModalitaFormeAmminControl.errors ? my_SrlModalitaFormeAmminControl.errors : {};

        // Obbligatorio quando, in vista SRL (CkNorme = 1) con tipo amministrazione CDA (CkSrlTipoAmministrazione = 1),
        // lo statuto prevede forme di amministrazione congiunte (CkSrlStatutoFormeAmmin = 1) o disgiunte (CkSrlStatutoFormeAmminDisg = 1).
        addRemoveError(
            isNilOrEmpty(my_SrlModalitaFormeAmminControl?.value) &&
                my_CkSrlTipoAmministrazione === '1' &&
                (my_CkSrlStatutoFormeAmmin === '1' || my_CkSrlStatutoFormeAmminDisg === '1') &&
                checkCkNormeValue(form),
            errors,
            'my_SrlModalitaFormeAmmin',
            { status: REQUIRED_STATUS }
        );

        my_SrlModalitaFormeAmminControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_SrlNoteComposConsAmmin: (form: FormGroup) => {
        normeSrlCkHelper(form, 'my_NormeSrl.my_SrlNoteComposConsAmmin', 'my_NormeSrl.my_CkSrlTipoAmministrazione', 1);
    },

    my_SrlNoteAmminUnico: (form: FormGroup) => {
        normeSrlCkHelper(form, 'my_NormeSrl.my_SrlNoteAmminUnico', 'my_NormeSrl.my_CkSrlTipoAmministrazione', 2);
    },

    my_SrlScadenzaAmministratori: (form: FormGroup) => {
        const my_SrlScadenzaAmministratoriControl = getNormeSrlControl(form, 'my_SrlScadenzaAmministratori');
        const my_CkSrlTipoAmministrazione = toNum(getNormeSrlControl(form, 'my_CkSrlTipoAmministrazione').value);
        const errors = my_SrlScadenzaAmministratoriControl.errors ? my_SrlScadenzaAmministratoriControl.errors : {};

        addRemoveError(
            toNum(my_SrlScadenzaAmministratoriControl?.value) !== 99 && my_CkSrlTipoAmministrazione === 3 && checkCkNormeValue(form),
            errors,
            'my_SrlScadenzaAmministratori',
            {
                status: 'Selezione incoerente',
            }
        );

        addRemoveError(isNilOrEmpty(my_SrlScadenzaAmministratoriControl?.value) && checkCkNormeValue(form), errors, 'my_SrlScadenzaAmministratori', {
            status: REQUIRED_STATUS,
        });

        my_SrlScadenzaAmministratoriControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkSrlCompStatoInOrgContr: (form: FormGroup) => {
        const my_CkSrlCompStatoInOrgContrControl = getNormeSrlControl(form, 'my_CkSrlCompStatoInOrgContr');
        const errors = my_CkSrlCompStatoInOrgContrControl.errors ? my_CkSrlCompStatoInOrgContrControl.errors : {};

        addRemoveError(
            isNilOrEmpty(my_CkSrlCompStatoInOrgContrControl?.value) && checkAllElemCollegioSindacale(form) && checkCkNormeValue(form),
            errors,
            'my_CkSrlCompStatoInOrgContr',
            {
                status: REQUIRED_STATUS,
            }
        );

        my_CkSrlCompStatoInOrgContrControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkSrlCompOrgContrEletti: (form: FormGroup) => {
        const my_CkSrlCompOrgContrElettiControl = getNormeSrlControl(form, 'my_CkSrlCompOrgContrEletti');
        const errors = my_CkSrlCompOrgContrElettiControl.errors ? my_CkSrlCompOrgContrElettiControl.errors : {};

        addRemoveError(
            isNilOrEmpty(my_CkSrlCompOrgContrElettiControl?.value) && checkAllElemCollegioSindacale(form) && checkCkNormeValue(form),
            errors,
            'my_CkSrlCompOrgContrEletti',
            {
                status: REQUIRED_STATUS,
            }
        );

        my_CkSrlCompOrgContrElettiControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkSrlRispettoLimite: (form: FormGroup) => {
        normeSrlCkHelper(
            form,
            'my_NormeSrl.my_CkSrlRispettoLimite',
            'my_NormeSrl.my_CkSrlCompOrgContrEletti',
            1,
            formPaths.ammControllo,
            checkAllElemCollegioSindacale(form)
        );
    },
};
